import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { UrgencyBadge } from "@/components/StatusBadge";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays } from "lucide-react";

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { api.get("/calendar").then(({ data }) => setEvents(data.events)).catch(() => setEvents([])); }, []);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const byDate = {};
  (events || []).forEach((e) => { if (e.date) { (byDate[e.date] = byDate[e.date] || []).push(e); } });
  const dstr = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const monthName = cursor.toLocaleString("default", { month: "long", year: "numeric" });
  const isBottom = user.role === "contractor";

  return (
    <DashboardLayout nav={getNav(user.role)} title="Schedule" bottomNav={isBottom}>
      {!events ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-head text-lg font-semibold flex items-center gap-2"><CalendarDays className="w-5 h-5 text-blue-600" /> {monthName}</h2>
            <div className="flex gap-2">
              <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setCursor(new Date())} className="px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Today</button>
              <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold text-slate-400 mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => (
              <div key={i} className={`min-h-[72px] md:min-h-[92px] rounded-lg border p-1 ${d ? "border-slate-100" : "border-transparent"}`}>
                {d && <div className="text-xs font-medium text-slate-500 mb-1">{d}</div>}
                {d && (byDate[dstr(d)] || []).map((e) => (
                  <button key={e.id} onClick={() => e.link && navigate(e.link)} className="w-full text-left mb-1 px-1.5 py-1 rounded bg-blue-50 hover:bg-blue-100 border border-blue-100">
                    <div className="text-[10px] font-medium text-blue-800 truncate">{e.title}</div>
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs text-slate-400">Scheduled work orders and contractor appointments appear here.</div>
        </div>
      )}
    </DashboardLayout>
  );
}
